import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import LoadingSpinner from "../components/common/LoadingSpinner";

/**
 * AdminAuth — route guard. Can be used two ways:
 *
 *   1. As a layout route (preferred, React Router v6+):
 *        <Route element={<AdminAuth><AdminLayout /></AdminAuth>}>
 *          <Route path="employees" element={<Employees />} />
 *          ...
 *        </Route>
 *
 *   2. As a wrapper:
 *        <AdminAuth><Dashboard /></AdminAuth>
 *
 * Round 2 improvements vs Round 1:
 *   - Uses context (AuthContext) instead of re-reading localStorage on every render
 *   - Waits for the initial token check (`loading` flag) before redirecting
 *   - Preserves the page the user tried to visit so we can redirect back
 *     after a successful login via location.state.from
 *   - Optional `roles` prop enforces role-based access at the route boundary
 */
export default function AdminAuth({ children, roles }) {
  const { userToken, loading, hasAnyRole } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner label="Checking session..." />;

  if (!userToken) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (roles && roles.length > 0 && !hasAnyRole(...roles)) {
    return <Navigate to="/admin/forbidden" replace />;
  }

  // If no children passed, render nested route outlet (layout-route mode).
  return children ?? <Outlet />;
}
